import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function RegisterPage() {
  const { register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", company_name: "", email: "", password: "", signature: "" });
  const [accept, setAccept] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!accept) {
      setError(t("auth.consentRequired"));
      return;
    }
    setLoading(true);
    try {
      const u = await register({ ...form, accept_terms: accept });
      toast.success(t("auth.created").replace("{name}", u.name));
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || t("auth.registerError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={t("auth.registerTitle")}
      subtitle={t("auth.registerSubtitle")}
      footer={
        <>
          {t("auth.haveAccount")}{" "}
          <Link to="/login" className="text-[#EAB308] hover:underline font-medium">
            {t("auth.loginLink")}
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <div className="space-y-2">
          <Label className="text-white/80">{t("auth.name")}</Label>
          <Input
            data-testid="register-name"
            required value={form.name} onChange={set("name")} placeholder={t("auth.namePh")}
            className="bg-[#0a0a0a] border-white/15 text-white h-11 placeholder:text-white/30"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white/80">{t("auth.company")}</Label>
          <Input
            data-testid="register-company"
            required value={form.company_name} onChange={set("company_name")} placeholder={t("auth.companyPh")}
            className="bg-[#0a0a0a] border-white/15 text-white h-11 placeholder:text-white/30"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white/80">{t("auth.email")}</Label>
          <Input
            data-testid="register-email"
            type="email" required value={form.email} onChange={set("email")} placeholder={t("auth.emailPh")}
            className="bg-[#0a0a0a] border-white/15 text-white h-11 placeholder:text-white/30"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white/80">{t("auth.password")}</Label>
          <Input
            data-testid="register-password"
            type="password" required minLength={6} value={form.password} onChange={set("password")} placeholder={t("auth.passwordPh")}
            className="bg-[#0a0a0a] border-white/15 text-white h-11 placeholder:text-white/30"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-white/80">{t("auth.signature")}</Label>
          <Input
            data-testid="register-signature"
            required value={form.signature} onChange={set("signature")} placeholder={t("auth.signaturePh")}
            className="bg-[#0a0a0a] border-white/15 text-white h-11 placeholder:text-white/30 font-[cursive] italic"
          />
        </div>

        <label className="flex items-start gap-3 cursor-pointer" data-testid="register-consent">
          <input
            type="checkbox"
            checked={accept}
            onChange={(e) => setAccept(e.target.checked)}
            className="mt-1 h-4 w-4 accent-[#EAB308]"
          />
          <span className="text-xs text-white/60 leading-relaxed">
            {t("auth.consentA")}
            <Link to="/conditions" target="_blank" rel="noopener noreferrer" className="text-[#EAB308] hover:underline">{t("auth.terms")}</Link>
            {t("auth.consentAnd")}
            <Link to="/confidentialite" target="_blank" rel="noopener noreferrer" className="text-[#EAB308] hover:underline">{t("auth.privacy")}</Link>{t("auth.consentEnd")}
          </span>
        </label>

        {error && (
          <div data-testid="register-error" className="rounded-md border border-[#dc143c]/40 bg-[#dc143c]/10 px-4 py-2.5 text-sm text-red-300">
            {error}
          </div>
        )}

        <Button
          data-testid="register-submit"
          type="submit" disabled={loading}
          className="w-full h-11 bg-[#EAB308] text-black font-semibold rounded-md hover:opacity-90 transition-opacity"
        >
          {loading ? t("auth.registerSubmitting") : t("auth.registerSubmit")}
        </Button>
      </form>
    </AuthShell>
  );
}
